import React from 'react';
import { FileText, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/Schema';

const TermsOfService = () => {
  const lastUpdated = "January 20, 2025";

  return (
    <div className="min-h-screen bg-gray-950">
      <SEO 
        title="Terms of Service"
        description="Terms of Service for TruckAccidentSupport.com. Read our terms and conditions for using this website."
        canonical="/terms-of-service"
        keywords="terms of service, terms and conditions, website terms, user agreement"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Terms of Service', path: '/terms-of-service' }
      ]} />

      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10">
                <FileText className="h-8 w-8 text-orange-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-300">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="text-gray-300 space-y-6 pt-6">
                <p>
                  Please read these Terms of Service ("Terms") carefully before using this website (the "Site"). By accessing or using this Site, you agree to be bound by these Terms. If you do not agree with these Terms, please do not use the Site.
                </p>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">1. Purpose of This Website</h3>
                  <p>
                    This website is an <strong>educational and informational resource</strong> focused on truck accident prevention, driver safety, legal awareness, and industry-related topics. The content provided is intended for general informational purposes only and should not be considered professional, legal, medical, or safety advice.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">2. No Legal or Professional Advice</h3>
                  <p>
                    The information on this Site does <strong>not</strong> constitute legal advice, safety advice, or professional guidance of any kind. Laws, regulations, and best practices vary by jurisdiction and situation.
                  </p>
                  <p>
                    You should always consult qualified professionals, including attorneys, safety consultants, or regulatory experts, before making decisions related to accidents, compliance, or legal matters.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">3. Use of Content</h3>
                  <p>
                    All content on this Site, including but not limited to articles, videos, graphics, downloads, and other materials, is provided for personal and informational use only.
                  </p>
                  <p>You may <strong>not</strong>:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Reproduce, republish, or distribute content without written permission</li>
                    <li>Modify or create derivative works based on Site content</li>
                    <li>Use content for unlawful or misleading purposes</li>
                  </ul>
                  <p>We reserve all rights not expressly granted.</p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">4. User Responsibilities</h3>
                  <p>By using this Site, you agree that you will:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Use the Site only for lawful purposes</li>
                    <li>Not attempt to disrupt or interfere with Site operations</li>
                    <li>Not upload or transmit malicious code, spam, or harmful content</li>
                    <li>Provide accurate information when submitting forms or subscribing</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">5. Email Subscriptions and Communications</h3>
                  <p>
                    By subscribing to our newsletter or downloading gated resources, you agree to receive email communications from us. You may unsubscribe at any time using the link provided in our emails.
                  </p>
                  <p>
                    We do not guarantee the frequency, timing, or content of communications.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">6. Downloadable Resources</h3>
                  <p>
                    Certain resources on this Site may be available only after email subscription. These resources are provided "as is" without warranties of any kind.
                  </p>
                  <p>
                    We do not guarantee accuracy, completeness, or suitability of downloadable materials for any specific purpose.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">7. Advertising and Third-Party Content</h3>
                  <p>
                    This Site may display advertisements, sponsored content, affiliate links, or third-party materials.
                  </p>
                  <p>We are <strong>not responsible</strong> for:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>The content, accuracy, or legality of third-party websites</li>
                    <li>Products or services offered by advertisers</li>
                    <li>Any transactions conducted with third parties</li>
                  </ul>
                  <p>Engagement with advertisers is solely between you and the advertiser.</p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">8. Affiliate Disclosure</h3>
                  <p>
                    Some links on this Site may be affiliate links, meaning we may earn a commission if you click or make a purchase. This does not affect the price you pay.
                  </p>
                  <p>
                    Affiliate relationships do not influence our editorial content.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">9. Intellectual Property</h3>
                  <p>
                    All intellectual property on this Site, including text, logos, branding, graphics, and design elements, is owned by or licensed to the Site owner and protected by applicable laws.
                  </p>
                  <p>
                    Unauthorized use may result in legal action.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">10. Disclaimer of Warranties</h3>
                  <p>
                    This Site is provided on an "as is" and "as available" basis.
                  </p>
                  <p>We make no warranties or representations regarding:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Accuracy or reliability of content</li>
                    <li>Availability or uninterrupted operation of the Site</li>
                    <li>Results obtained from using the Site</li>
                  </ul>
                  <p>
                    To the fullest extent permitted by law, all warranties are disclaimed.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">11. Limitation of Liability</h3>
                  <p>
                    To the maximum extent permitted by law, we shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of or related to your use of this Site.
                  </p>
                  <p>
                    This includes, without limitation, damages related to accidents, legal claims, business losses, or reliance on information provided.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">12. Indemnification</h3>
                  <p>
                    You agree to indemnify and hold harmless the Site owner, affiliates, partners, and contributors from any claims, liabilities, damages, or expenses arising from:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Your use of the Site</li>
                    <li>Violation of these Terms</li>
                    <li>Infringement of third-party rights</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">13. Modifications to These Terms</h3>
                  <p>
                    We reserve the right to update or modify these Terms at any time without prior notice. Changes will be effective upon posting to this page.
                  </p>
                  <p>
                    Continued use of the Site constitutes acceptance of revised Terms.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">14. Governing Law</h3>
                  <p>
                    These Terms shall be governed by and interpreted in accordance with the laws of the applicable jurisdiction, without regard to conflict of law principles.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">15. Contact Information</h3>
                  <p>
                    If you have questions about these Terms, please contact us through the Contact page on this Site.
                  </p>
                </div>

                <div className="mt-8 p-6 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-center font-semibold text-white">
                    By using this Site, you acknowledge that you have read, understood, and agree to these Terms of Service.
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
